import { useEffect, useState, useSyncExternalStore } from 'react'
import { User } from '../../../types'
import { loadUser } from '../../actions/loadUser'
import { socketLookupUser } from '../../actions/socketLookupUser'
import { loadUserExtras } from '../../actions/user'
import { SecureStateType, UserEvent } from '../../constants/enums'
import { getStateUser, getUser, resolveID } from '../../state/getState'
import { pubSubUsers } from '../../state/pubSub'
import { filterAndReplaceBadWords } from '../../utilities/ageGate'
import { useSecureState } from './useSecureState'

// Fields that are always filtered for bad words when age gate is active
const AUTO_FILTER_FIELDS = new Set<keyof User>([
    'topic',
    'blurb',
    'notes',
    'latestPMText',
    'shareRecentAlbumTitle',
    'shareRecentClubTitle',
    'shareRecentCollectionTitle',
    'shareRecentGoalsTitle',
    'shareRecentItemTitle',
    'shareRecentPollTitle',
])

type userSelectorType<
    B extends boolean,
    T extends keyof User,
    J extends keyof User[T],
> = B extends true ? User[T][J] : User[T]

const getUserSelector =
    <T extends keyof User, K extends boolean, J extends keyof User[T]>(
        id: number,
        key: T,
        deepKey: K,
        deepAttribute?: J,
    ) =>
    (): userSelectorType<K, T, J> =>
        deepKey ? getUser(id)?.[key]?.[deepAttribute] : getUser(id)?.[key]

const subUserAttribute = (id: number, attribute: string) => (callback) => {
    if (id === undefined) {
        // don't actually sub if we don't have an id yet -
        // Connections, PinDragHelper, and QuickMenu (potentially others) may all trigger this during normal use
        return () => {}
    }
    return pubSubUsers.on(
        UserEvent.UPDATE + '::' + id + '::' + attribute,
        callback,
    )
}

/**
 * Retrieves user attribute from `USERSSTATE`. Does not trigger any lookups by default
 */
export const useUserSelector = <
    T extends keyof User,
    J extends keyof User[T],
    B extends boolean = false,
>(
    slugOrId: string | number | 'me',
    attribute: T,
    { socketLookup = false, lookup = false } = {},
    deepKey?: B, // adding this was the easiest way I could find to ensure types worked
    deepAttribute?: J,
): userSelectorType<B, T, J> => {
    const [foundId, setFoundId] = useState(resolveID(slugOrId))
    const { ageGateActive } = useSecureState(SecureStateType.AGE_VERIFICATION)

    // waits on a lookup under way; also when this hook stops looking up
    // itself, as that drops its own wait on the lookup it started
    useEffect(() => {
        if (!foundId && USERS_LOADING[slugOrId]) {
            // ignored once this hook has moved on (another user, or unmounted)
            let current = true
            USERS_LOADING[slugOrId].push((id: number) => {
                if (current) setFoundId(id)
            })
            return () => {
                current = false
            }
        }
    }, [foundId, slugOrId, lookup, socketLookup])

    useEffect(() => {
        const sync = () => setFoundId(resolveID(slugOrId))
        sync()
        if (slugOrId !== 'me') return
        // 'me' is registered asynchronously (visitor fetch, worker index) and the
        // event fires for guests too (id 0) - never gate this on a truthy id
        return pubSubUsers.on(UserEvent.ME_ID, sync)
    }, [slugOrId])

    useEffect(() => {
        if ((socketLookup || lookup) && slugOrId) {
            // check if the model exists and then do a lookup if not
            const id = resolveID(slugOrId)
            // ignored once this hook has moved on (another user, or unmounted)
            let current = true
            const setFoundIdAndState = (id: number) => {
                if (current) setFoundId(id)
            }
            const exists = id ? !!getStateUser(id) : false
            if (exists) {
                setFoundId(id)
            } else if (!USERS_LOADING[slugOrId]) {
                USERS_LOADING[slugOrId] = [setFoundIdAndState]
                const lookupUser = socketLookup ? socketLookupUser : loadUser
                lookupUser(slugOrId).then((found) => {
                    const id = resolveID(slugOrId)
                    USERS_LOADING[slugOrId].forEach((callback) =>
                        callback(id, !!found),
                    )

                    delete USERS_LOADING[slugOrId]
                })
            } else if (USERS_LOADING[slugOrId]) {
                // if we're loading then we need to ensure we still get subscribed
                USERS_LOADING[slugOrId].push(setFoundIdAndState)
            }
            return () => {
                current = false
            }
        }
    }, [slugOrId, socketLookup, lookup])

    const getSnapshot = getUserSelector(
        foundId,
        attribute,
        deepKey,
        deepAttribute,
    )

    const value = useSyncExternalStore(
        subUserAttribute(foundId, attribute),
        getSnapshot,
        getSnapshot,
    )

    // skip filtering if age gate isn't active or it's a deep key lookup
    if (!ageGateActive || deepKey) {
        return value
    }

    // auto filter fields that always need filtering
    if (AUTO_FILTER_FIELDS.has(attribute) && typeof value === 'string') {
        return filterAndReplaceBadWords(value) as userSelectorType<B, T, J>
    }

    return value
}

// keep track of loading models so we don't accidentally load twice. Each
// waiting hook is called with the id and whether the lookup found anyone
const USERS_LOADING: Record<
    string | number,
    Array<(id: number, found: boolean) => void>
> = {}

export const useUserExists = (
    slugOrId: string | number,
    { socketLookup = false, loadExtras = false } = {},
): [loading: boolean, exists: boolean] => {
    const [userId, setUserId] = useState(() => getUser(slugOrId)?.id)

    const [loading, setLoading] = useState(() => !userId)
    const [extras, setExtras] = useState(false)
    const [exists, setExists] = useState(() => (!userId ? undefined : true))

    useEffect(() => {
        if (!slugOrId) return
        // a lookup answering once this hook has moved on (another user, or
        // unmounted) is ignored
        let current = true
        const setIdAndState = (id: number, found: boolean) => {
            if (!current) return
            setUserId(found ? id : undefined)
            setLoading(false)
            setExists(found)
        }
        if (!USERS_LOADING[slugOrId]) {
            const lookupUser = socketLookup ? socketLookupUser : loadUser
            const id = resolveID(slugOrId)
            const existsInState = id ? !!getStateUser(id) : false
            if (!existsInState) {
                USERS_LOADING[slugOrId] = [setIdAndState]
                lookupUser(slugOrId).then((exists) => {
                    if (exists) {
                        const u = getStateUser(resolveID(slugOrId))
                        if (u) {
                            USERS_LOADING[slugOrId].forEach((callback) =>
                                callback(u.id, true),
                            )
                        }
                    } else {
                        USERS_LOADING[slugOrId].forEach((callback) =>
                            callback(resolveID(slugOrId), false),
                        )
                    }

                    delete USERS_LOADING[slugOrId]
                })
            } else {
                setExists(true)
                setLoading(false)
                setUserId(id)
            }
        } else if (USERS_LOADING[slugOrId]) {
            // if we're loading then we need to ensure we still get updated,
            // found or not (a hook mounted mid-lookup, e.g. remounted)
            USERS_LOADING[slugOrId].push(setIdAndState)
        }
        return () => {
            current = false
        }
    }, [slugOrId, userId, socketLookup])

    useEffect(() => {
        /// if the id changes we need to reset the state, so old/incorrect values aren't provided
        const existingId = getUser(slugOrId)?.id
        setUserId(() => existingId)
        setExists(() => (!existingId ? undefined : true))
    }, [slugOrId])

    useEffect(() => {
        if (!userId) return
        return () => {
            // ID has changed so let's reset the loading extras state
            setExtras(false)
        }
    }, [userId])

    useEffect(() => {
        if (!userId) return
        if (!extras && !getUser(slugOrId)?.extras && loadExtras) {
            setExtras(true)
            loadUserExtras(userId)
        }
    }, [slugOrId, userId, extras, loadExtras])

    return [loading, exists]
}
